import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import {
  DpeState,
  EntityStatus,
  PropertyCreationStep,
  PropertyDocumentStatus,
  PropertyDocumentType,
  PropertyDto,
  PropertyVerificationStatus,
} from '@workspace/data-types';
import { AlertSnackBarService } from '@workspace/ng-aura';
import { ApiService } from '@workspace/ng-http';
import { catchError, filter, of, switchMap, tap } from 'rxjs';
import { EtxeEnumsNiceLabelUtils } from '../../../utils/etxe-enums-nice-label.utils';
import { MissingPropertyInfo, PropertyMissingInfoUtils } from '../../utils/property-missing-info.utils';
import { EtxePropertyMissingInfoComponent } from '../property-missing-info/property-missing-info.component';
import { PropertyPublishConfirmationDialogComponent } from '../property-publish-dialog/property-publish-confirmation-dialog.component';
import {
  DEFAULT_PROPERTY_NAV_SECTIONS,
  EtxePropertySectionNavComponent,
  PropertyNavSection,
} from '../property-section-nav/property-section-nav.component';

export type { MissingPropertyInfo };

@Component({
  selector: 'etxe-property-status-header',
  imports: [
    CommonModule,
    RouterLink,
    MatIcon,
    MatButtonModule,
    MatMenuModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    EtxePropertySectionNavComponent,
    EtxePropertyMissingInfoComponent,
  ],
  templateUrl: './property-status-header.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class EtxePropertyStatusHeaderComponent {
  private elementRef = inject(ElementRef);
  private dialog = inject(MatDialog);
  private api = inject(ApiService);
  private alertSnackBar = inject(AlertSnackBarService);
  private destroyRef = inject(DestroyRef);

  property = input.required<PropertyDto>();
  editable = input<boolean>(false);
  sections = input<PropertyNavSection[]>(DEFAULT_PROPERTY_NAV_SECTIONS);

  editRequested = output<{ step?: PropertyCreationStep; anchorId: string }>();
  manageSettings = output<void>();
  propertyUpdated = output<PropertyDto>();

  isPublishing = signal<boolean>(false);

  onManageSettings(): void {
    this.manageSettings.emit();
  }

  onPublishClick(): void {
    const prop = this.property();
    if (!prop?._id || !this.isReadyToPublish()) return;

    const dialogRef = this.dialog.open(PropertyPublishConfirmationDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
    });

    dialogRef
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((confirmed): confirmed is boolean => !!confirmed),
        tap(() => this.isPublishing.set(true)),
        switchMap(() =>
          this.api
            .business()
            .etxe()
            .properties()
            .owner()
            .activate(prop._id)
            .pipe(
              catchError((err) => {
                console.error('Error activating property:', err);
                this.isPublishing.set(false);
                this.alertSnackBar.error({
                  message: "Erreur lors de la publication de l'annonce.",
                });
                return of(null);
              }),
            ),
        ),
      )
      .subscribe((updated) => {
        this.isPublishing.set(false);
        if (updated) {
          this.propertyUpdated.emit(updated);
          this.alertSnackBar.success({
            message: 'Félicitations ! Votre annonce est désormais en ligne.',
          });
        }
      });
  }

  EntityStatus = EntityStatus;
  PropertyVerificationStatus = PropertyVerificationStatus;
  PropertyDocumentStatus = PropertyDocumentStatus;
  PropertyDocumentType = PropertyDocumentType;
  PropertyCreationStep = PropertyCreationStep;
  DpeState = DpeState;

  isScrolled = signal<boolean>(false);
  drawerExpanded = signal<boolean>(false);

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (typeof window !== 'undefined') {
      this.isScrolled.set(window.scrollY > 40);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.drawerExpanded()) return;
    const target = event.target as HTMLElement;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.drawerExpanded.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.drawerExpanded()) {
      this.drawerExpanded.set(false);
    }
  }

  verificationStatus = computed(
    () => this.property()?.verification?.owner ?? PropertyVerificationStatus.UNVERIFIED,
  );
  isVerified = computed(() => this.verificationStatus() === PropertyVerificationStatus.VERIFIED);

  ownershipDoc = computed(() => {
    const ownershipDocs =
      this.property()?.documents?.filter((d) => d.type === PropertyDocumentType.OWNERSHIP) || [];
    return ownershipDocs.length > 0 ? ownershipDocs[ownershipDocs.length - 1] : undefined;
  });

  isOwnershipPendingReview = computed(
    () => this.ownershipDoc()?.status === PropertyDocumentStatus.PENDING_REVIEW,
  );

  hasBeenPublished = computed(() => !!this.property()?.state?.firstActivatedAt);

  isReadyToPublish = computed(
    () =>
      this.isVerified() &&
      this.property()?.state?.status === EntityStatus.DEACTIVATED &&
      !this.hasBeenPublished(),
  );

  isActionRequiredToPublish = computed(
    () =>
      !this.isVerified() &&
      !this.isOwnershipPendingReview() &&
      this.verificationStatus() === PropertyVerificationStatus.UNVERIFIED,
  );

  showPublicationStatus = computed(() => !this.isActionRequiredToPublish());

  offerTypeLabel = computed(() => {
    const offerType = this.property()?.definition?.offerType;
    return offerType ? EtxeEnumsNiceLabelUtils.offerType(offerType) : null;
  });

  propertyTypeLabel = computed(() => {
    const propType = this.property()?.definition?.propertyType;
    return propType ? EtxeEnumsNiceLabelUtils.propertyType(propType) : null;
  });

  residenceTypeLabel = computed(() => {
    const resType = this.property()?.definition?.residenceType;
    return resType ? EtxeEnumsNiceLabelUtils.residenceType(resType) : null;
  });

  propertyTitle = computed(() => {
    const prop = this.property();
    if (prop?.presentation?.title?.trim()) {
      return prop.presentation.title.trim();
    }
    const typeLabel = prop?.definition?.propertyType ? prop.definition.propertyType : 'Bien';
    const city = prop?.location?.address?.city;
    return city ? `${typeLabel} à ${city}` : typeLabel;
  });

  locationSummary = computed(() => {
    const addr = this.property()?.location?.address;
    if (!addr) return '';
    const parts = [addr.city, addr.zipCode].filter(Boolean);
    return parts.join(', ');
  });

  priceFormatted = computed(() => {
    const priceCents = this.property()?.financials?.sellingPriceCents;
    if (!priceCents) return null;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(priceCents / 100);
  });

  pricePerSquareMeterFormatted = computed(() => {
    const priceCents = this.property()?.financials?.sellingPriceCents;
    const habitable = this.property()?.keyInfo?.surface?.habitable;
    if (!priceCents || !habitable || habitable <= 0) return null;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(priceCents / 100 / habitable);
  });

  surfaceFormatted = computed(() => {
    const habitable = this.property()?.keyInfo?.surface?.habitable;
    return habitable ? `${habitable} m²` : null;
  });

  readonly totalEvaluatedChecks = PropertyMissingInfoUtils.TOTAL_EVALUATED_CHECKS;

  missingItems = computed<MissingPropertyInfo[]>(() => {
    return PropertyMissingInfoUtils.getAllMissingItems(this.property());
  });

  completedChecks = computed(() => Math.max(0, this.totalEvaluatedChecks - this.missingItems().length));

  completionPercentage = computed(() => {
    return Math.round((this.completedChecks() / this.totalEvaluatedChecks) * 100);
  });

  toggleDrawer(): void {
    this.drawerExpanded.update((v) => !v);
  }

  onActionClicked(item: MissingPropertyInfo): void {
    this.drawerExpanded.set(false);
    this.editRequested.emit({ step: item.step, anchorId: item.anchorId });
    const el = document.getElementById(item.anchorId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  scrollToDocuments(): void {
    this.drawerExpanded.set(false);
    this.editRequested.emit({ anchorId: 'documents-section' });
    const el = document.getElementById('documents-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
